import React, { useEffect, useState } from 'react';
import { Clock3, RotateCcw, Trash2 } from 'lucide-react';
import type { RevisionRecord } from '../types';
import { useEditorStore } from '../store/editor';
import { deleteRevision, listRevisions } from '../storage/db';
import { CellSvg } from '../render/CellSvg';
import { Modal } from './Modal';

export const RevisionsDialog: React.FC = () => {
  const project = useEditorStore((state) => state.project);
  const open = useEditorStore((state) => state.revisionsOpen);
  const setOpen = useEditorStore((state) => state.setRevisionsOpen);
  const setProject = useEditorStore((state) => state.setProject);
  const [revisions, setRevisions] = useState<RevisionRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !project) return;
    setLoading(true);
    void listRevisions(project.id).then((items) => {
      setRevisions(items);
      setLoading(false);
    });
  }, [open, project]);

  const remove = async (id: string) => {
    await deleteRevision(id);
    setRevisions((items) => items.filter((item) => item.id !== id));
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)} title="Revision history" subtitle="Manual snapshots are kept locally in this browser." className="revision-modal">
      {loading ? <div className="empty-state"><div className="spinner" /> Loading revisions…</div> : revisions.length === 0 ? (
        <div className="empty-state"><Clock3 size={36} /><h3>No saved revisions</h3><p>Use “Save revision” in the inspector whenever you reach a useful milestone.</p></div>
      ) : (
        <div className="revision-list">
          {revisions.map((revision) => (
            <article className="revision-card" key={revision.id}>
              <div className="revision-preview"><CellSvg project={revision.document} cell={revision.document.cells[0]} preserveAspectRatio="xMidYMid slice" /></div>
              <div className="revision-copy"><strong>{revision.label}</strong><span>{new Date(revision.createdAt).toLocaleString()}</span><small>{revision.document.cells.length} scenes · {revision.document.title}</small></div>
              <div className="revision-actions">
                <button type="button" className="small-button" onClick={() => {
                  if (!window.confirm('Restore this revision? Your current state will remain in autosave until the restored version is saved.')) return;
                  setProject(revision.document, true);
                  setOpen(false);
                }}><RotateCcw size={16} /> Restore</button>
                <button type="button" className="icon-button danger" onClick={() => void remove(revision.id)} aria-label="Delete revision"><Trash2 size={16} /></button>
              </div>
            </article>
          ))}
        </div>
      )}
    </Modal>
  );
};
